import React, { useState, useEffect, useMemo } from 'react';
import { Building2, TrendingUp, AlertTriangle, CreditCard } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import localApiAdapter from '../../core/services/localApiAdapter';
import ModuleAnalyticsSummaryPage from '../../shared/components/analytics/ModuleAnalyticsSummaryPage';
import { formatBrandMoney, getBrandCurrencySuffix } from '../../shared/utils/brandCurrency';
import { formatBankingExportRows, getBankingExportCurrencyCode } from './bankingHelpers';
import { formatChartMonthKey } from '../../shared/utils/chartFormatters';

const DEFAULT_DATA = {
  accounts: { totalAccounts: 0, activeAccounts: 0, totalBalance: 0 },
  statements: { totalStatements: 0, reconciledStatements: 0, unreconciledStatements: 0 },
  reconciliations: { totalReconciliations: 0, completedReconciliations: 0, pendingReconciliations: 0 },
  pendingChecks: { total: 0, pending: 0 },
  discrepancies: { totalDiscrepancies: 0, totalAmount: 0 },
  cashFlow: [],
  balanceByAccount: [],
};

function mapBankingDashboard(raw) {
  if (!raw || typeof raw !== 'object') return DEFAULT_DATA;

  if (raw.kpis) {
    const kpis = raw.kpis;
    const pendingRecon = kpis.pendingReconciliations ?? 0;
    const checksOutstanding = kpis.checksOutstanding ?? 0;
    const reconciled =
      raw.statements?.reconciledStatements
      ?? raw.reconciliations?.completedReconciliations
      ?? Math.max(0, (kpis.accountsCount ?? 0) - pendingRecon);
    const pending =
      raw.statements?.unreconciledStatements
      ?? raw.reconciliations?.pendingReconciliations
      ?? pendingRecon;

    return {
      ...DEFAULT_DATA,
      ...raw,
      accounts: {
        totalAccounts: kpis.accountsCount ?? 0,
        activeAccounts: kpis.accountsCount ?? 0,
        totalBalance: kpis.totalBalance ?? 0,
      },
      statements: raw.statements || {
        totalStatements: reconciled + pending,
        reconciledStatements: reconciled,
        unreconciledStatements: pending,
      },
      reconciliations: {
        totalReconciliations: reconciled + pending,
        completedReconciliations: reconciled,
        pendingReconciliations: pendingRecon,
      },
      pendingChecks: raw.pendingChecks || { total: checksOutstanding, pending: checksOutstanding },
      balanceByAccount: (raw.balanceByAccount?.length ? raw.balanceByAccount : raw.chartRows || []).map((row) => ({
        account_name: row.account_name || row.label || row.name,
        account_number: row.account_number,
        bank_name: row.bank_name,
        balance: row.balance ?? row.value ?? 0,
        currency: row.currency,
        status: row.status,
      })),
      cashFlow: raw.cashFlow || [],
    };
  }

  return { ...DEFAULT_DATA, ...raw };
}

const BankingAnalysesRapportsPage = () => {
  const { t } = useTranslation();
  const p = (key, opts) => t(`modules.banque.pages.bankingAnalysesRapportsPage.${key}`, opts);
  const currencySuffix = getBrandCurrencySuffix();
  const formatAmount = (amount) => formatBrandMoney(amount);

  const [report, setReport] = useState(DEFAULT_DATA);
  const [loading, setLoading] = useState(true);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const loadReport = async () => {
    setLoading(true);
    try {
      const dashboard = await localApiAdapter.get('/banking/dashboard');
      setReport(mapBankingDashboard(dashboard));
    } catch (error) {
      console.error('Banking analyses load error:', error);
      toast.error(error.message || p('load_error', { defaultValue: 'تعذّر تحميل التحليلات' }));
      setReport(DEFAULT_DATA);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReport();
  }, [refreshTrigger]);

  const accounts = report.accounts || DEFAULT_DATA.accounts;
  const statements = report.statements || DEFAULT_DATA.statements;
  const reconciliations = report.reconciliations || DEFAULT_DATA.reconciliations;
  const pendingChecks = report.pendingChecks || DEFAULT_DATA.pendingChecks;

  const reconciledCount =
    statements.reconciledStatements ?? reconciliations.completedReconciliations ?? 0;
  const pendingReconCount =
    statements.unreconciledStatements ?? reconciliations.pendingReconciliations ?? 0;
  const reconciliationTotal =
    statements.totalStatements
    ?? reconciliations.totalReconciliations
    ?? reconciledCount + pendingReconCount;
  const reconciliationRate =
    reconciliationTotal > 0 ? Math.round((reconciledCount / reconciliationTotal) * 100) : 0;
  const pendingItems =
    pendingReconCount + (pendingChecks.total ?? pendingChecks.pending ?? 0);

  const insights = useMemo(
    () => [
      {
        label: p('insight_reconciliation_rate'),
        value: loading ? '…' : `${reconciliationRate}%`,
      },
      {
        label: p('insight_total_balance'),
        value: loading ? '…' : formatAmount(accounts.totalBalance || 0),
      },
      {
        label: p('insight_pending_items'),
        value: loading ? '…' : pendingItems,
        hint: p('insight_pending_items_hint', {
          recon: pendingReconCount,
          checks: pendingChecks.total ?? pendingChecks.pending ?? 0,
        }),
      },
    ],
    [loading, reconciliationRate, accounts.totalBalance, pendingItems, pendingReconCount, pendingChecks, p]
  );

  const stats = useMemo(
    () => [
      { label: p('stat_balance'), value: loading ? '…' : formatAmount(accounts.totalBalance || 0), icon: Building2 },
      { label: p('stat_accounts'), value: loading ? '…' : accounts.totalAccounts || 0, icon: CreditCard },
      { label: p('stat_reconciled'), value: loading ? '…' : statements.reconciledStatements || 0, icon: TrendingUp },
      { label: p('stat_pending_recon'), value: loading ? '…' : pendingReconCount, icon: AlertTriangle },
    ],
    [loading, accounts, statements, pendingReconCount, p]
  );

  const charts = useMemo(() => {
    const cashFlowRows = [...(report.cashFlow || [])]
      .slice(0, 6)
      .reverse()
      .map((row) => ({
        label: formatChartMonthKey(row.month, { fallback: p('na') }) || p('na'),
        value: row.netFlow || 0,
        display: formatAmount(row.netFlow || 0),
      }));

    const accountRows = [...(report.balanceByAccount || [])]
      .sort((a, b) => Math.abs(b.balance || 0) - Math.abs(a.balance || 0))
      .slice(0, 6)
      .map((row) => ({
        label: row.account_name || row.bank_name || p('na'),
        value: Math.abs(row.balance || 0),
        display: formatAmount(row.balance || 0),
      }));

    return [
      {
        title: p('chart_cashflow'),
        subtitle: p('chart_cashflow_subtitle'),
        alwaysShow: true,
        rows: cashFlowRows,
      },
      {
        title: p('chart_by_account'),
        subtitle: p('chart_by_account_subtitle'),
        variant: 'ranked',
        alwaysShow: true,
        rows: accountRows,
      },
      {
        title: p('chart_reconciliation_status'),
        subtitle: p('chart_reconciliation_subtitle'),
        variant: 'status',
        active: reconciledCount,
        inactive: pendingReconCount,
        alwaysShow: true,
      },
    ];
  }, [report, reconciledCount, pendingReconCount, p]);

  const table = useMemo(() => {
    const rawRows = (report.balanceByAccount || []).map((row) => ({
      account_name: row.account_name,
      account_number: row.account_number,
      bank_name: row.bank_name,
      balance: row.balance,
      currency: row.currency,
      status: row.status,
    }));
    const formatted = formatBankingExportRows(rawRows);

    return {
      title: p('table_accounts'),
      columns: [
        { key: 'account_name', label: p('col_account') },
        { key: 'account_number', label: p('col_number') },
        { key: 'bank_name', label: p('col_bank') },
        { key: 'balance', label: p('col_balance_sar', { defaultValue: `${p('col_balance')} (${currencySuffix})` }) },
        { key: 'currency', label: p('col_currency') },
        { key: 'status', label: p('col_status') },
      ],
      rows: formatted.map((row) => ({
        account_name: row.account_name || p('na'),
        account_number: row.account_number || p('na'),
        bank_name: row.bank_name || p('na'),
        balance: formatAmount(row.balance),
        currency: row.currency || getBankingExportCurrencyCode(),
        status: row.status || p('na'),
      })),
    };
  }, [report, p, currencySuffix]);

  return (
    <ModuleAnalyticsSummaryPage
      title={p('title')}
      subtitle={p('subtitle')}
      loading={loading}
      onRefresh={() => setRefreshTrigger((n) => n + 1)}
      stats={stats}
      insights={insights}
      charts={charts}
      table={table}
      exportFilePrefix="banking_analyses"
      emptyLabel={p('no_data')}
    />
  );
};

export default BankingAnalysesRapportsPage;
